import { useArticleStore } from '~/store/article';
import { type InlineVideo } from '~/types/articleInline';
import { type PublicationContext, type WindowIncludingLoadedScript } from '~/types';

export default defineNuxtPlugin((nuxt) => {
  nuxt.hook('cpp:data:loaded', ({ type }: {type: string}) => {
    if (type !== 'article') {
      return;
    }

    const article = useArticleStore().currentArticle;
    const { jwplayer } = useRuntimeConfig().public as unknown as PublicationContext;
    const windowWithLoadedScript = window as WindowIncludingLoadedScript;
    const loadedScripts = (windowWithLoadedScript.loadedScripts ??= {});

    /*
     * Autoplay videos use dedicated JW player builds with autoplay +
     * mute baked in, so the variant depends on siteName AND autoPlay.
     * Publications without an autoplay build fall back to the standard
     * variant — same src and key, so the library is never injected twice.
     * DnVideo derives the same key; keep both sides in sync.
     */
    const loadScript = (siteName?: string, autoPlay?: boolean) => {
      const orientation = siteName === 'dn_vertical' ? 'vertical' : 'horizontal';
      const variant = autoPlay && jwplayer?.[`${orientation}AutoplaySrc`] ? `${orientation}Autoplay` : orientation;
      const src = jwplayer?.[`${variant}Src`];
      /* One key for both the useHead dedupe and the loadedScripts entry. */
      const key = `jwplayer${variant.charAt(0).toUpperCase()}${variant.slice(1)}`;

      /* Create the ref before injecting so DnVideo's watcher can track it. */
      const loaded = (loadedScripts[key] ??= ref<boolean>(false));
      if (!src || loaded.value) {
        return;
      }

      useHead({
        script: [
          {
            src,
            key,
            onload: () => {
              loaded.value = true;
            }
          }
        ]
      });
    };

    const leadAsset = article.leadAsset as InlineVideo | undefined;
    if (leadAsset?.type === 'video') {
      loadScript(leadAsset.siteName, leadAsset.autoPlay);
    }

    article.body?.forEach((item) => {
      if (item.type === 'video') {
        loadScript(item.siteName, item.autoPlay);
      }
    });
  });
});
